import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/apiClient';
import { ensureClientWikiImageCookies } from '../../utils/wikiImageCookies';
import { sanitiseWikiHtml } from '../../utils/wikiSanitise';
import WikiTreeNav from './components/WikiTreeNav';
import { useProjectWebSocket } from '../../hooks/useProjectWebSocket';

const ClientWiki = () => {
    const [loading, setLoading] = useState(true);
    const [wikiPages, setWikiPages] = useState([]);
    const [selectedPage, setSelectedPage] = useState(null);
    const [error, setError] = useState('');
    const [projectName] = useState('Project');
    const navigate = useNavigate();

    const email = localStorage.getItem('clientEmail');
    const token = localStorage.getItem('clientToken');
    const leadId = localStorage.getItem('clientLeadId');
    const timestamp = localStorage.getItem('clientTimestamp');

    // Placeholder substitution first, then sanitise - the string handed to
    // dangerouslySetInnerHTML is always the sanitised one. The policy itself
    // lives in utils/wikiSanitise.js, shared byte-for-byte with the Lambda
    // layer; nothing about it is repeated here.
    const processWikiHtml = (html) => {
        if (!html) return '';

        const withPlaceholders = html.replace(
            /<img([^>]*)src="(https?:\/\/project\.themigrationexperts\.com[^"]+)"([^>]*)>/g,
            '<div style="background:#f9f9f9; border:1px solid #e0e0e0; padding:12px; margin:12px 0; text-align:center; color:#666; font-size:13px; border-radius:4px;">Image not available here</div>'
        );

        return sanitiseWikiHtml(withPlaceholders);
    };

    useEffect(() => {
        if (!token || !email || !leadId || !timestamp) {
            navigate('/client/login');
            return;
        }

        fetchWikiPages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, email, leadId, timestamp, navigate]);

    const fetchWikiPages = useCallback(async () => {
        try {
            // Wiki images are served with CloudFront signing enforced, so the
            // signed cookies must be in place before any <img> in the page HTML
            // is fetched by the browser. leadId is only a local cache key - the
            // project the cookie covers is taken from the token server side.
            // Failing loudly beats rendering a page full of broken images.
            if (!await ensureClientWikiImageCookies(leadId)) {
                throw new Error('Failed to authorise wiki images');
            }

            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/wiki?leadId=${encodeURIComponent(leadId)}&timestamp=${encodeURIComponent(timestamp)}`
            );

            if (response.ok) {
                const data = await response.json();

                const pagesArray = Array.isArray(data.pages) ? data.pages : [];
                const sortedPages = pagesArray.sort((a, b) => {
                    const orderA = typeof a.order === 'number' ? a.order : 0;
                    const orderB = typeof b.order === 'number' ? b.order : 0;
                    return orderA - orderB;
                });
                setWikiPages(sortedPages);

                if (sortedPages.length > 0) {
                    setSelectedPage(current => {
                        if (!current) {
                            // Restore the page this user was last reading, the
                            // way every other client view restores its state.
                            const savedId = localStorage.getItem(`clientWikiPage_${leadId}`);
                            const saved = savedId ? sortedPages.find(p => p.pageId === savedId) : null;
                            if (saved) {
                                return saved;
                            }
                            const rootPages = sortedPages.filter(p => !p.parentId);
                            return rootPages.length > 0 ? rootPages[0] : sortedPages[0];
                        }
                        const stillExists = sortedPages.find(p => p.pageId === current.pageId);
                        if (stillExists) {
                            return stillExists;
                        }
                        const rootPages = sortedPages.filter(p => !p.parentId);
                        return rootPages.length > 0 ? rootPages[0] : sortedPages[0];
                    });
                }
                setError('');
            } else {
                setError('Failed to load wiki pages');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [leadId, timestamp]);

    // The one WebSocket implementation, shared with the Kanban, the Gantt and the
    // admin Dashboard. This page used to open its own socket with only leadId and
    // timestamp, which stopped working the moment $connect gained an authorizer -
    // the token was never sent, so the handshake was refused and the page silently
    // stopped live-updating. Two implementations is how that happens.
    //
    // Placed BELOW fetchWikiPages deliberately: it is referenced during render,
    // and a const is in the temporal dead zone above its own declaration.
    useProjectWebSocket({
        leadId,
        timestamp,
        // getToken, not the captured token: reconnects hours in must carry the
        // CURRENT token or the authorizer refuses them forever (BUG-030)
        getToken: () => localStorage.getItem('clientToken'),
        enabled: Boolean(token && leadId && timestamp),
        connectDelayMs: 100,
        onMessage: (message) => {
            if (message.type === 'project_updated') {
                fetchWikiPages();
            }
        }
    });

    const handlePageSelect = (page) => {
        setSelectedPage(page);
        localStorage.setItem(`clientWikiPage_${leadId}`, page.pageId);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading Wiki...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {error && (
                <div className="bg-red-50 border-b border-red-200">
                    <div className="max-w-7xl mx-auto px-4 py-2">
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                </div>
            )}

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
                <div className="grid md:grid-cols-4 gap-6">
                    <div className="md:col-span-1">
                        <div className="bg-white rounded-lg shadow p-4">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                {projectName} Wiki
                            </h2>
                            <WikiTreeNav
                                pages={wikiPages}
                                selectedPageId={selectedPage?.pageId}
                                onSelect={handlePageSelect}
                                storageKey={`clientWikiTree_${leadId}`}
                            />
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        {selectedPage ? (
                            <div className="bg-white rounded-lg shadow p-8">
                                <h1 className="text-3xl font-bold text-gray-900 mb-6">
                                    {selectedPage.title}
                                </h1>
                                <div
                                    className="wiki-doc"
                                    dangerouslySetInnerHTML={{ __html: processWikiHtml(selectedPage.contentHtml) }}
                                />
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg shadow p-12 text-center">
                                <p className="text-gray-500">Select a wiki page to view</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientWiki;
